import * as React from 'react';

import { Unit } from '@rematter/frontend-lib-api-client';
import {
  cn,
  humanReadableShortPricingUnit,
  TouchButton,
  TouchNumberField,
} from '@rematter/ui-web';
import { LucideTrash2 } from 'lucide-react';

import { NormalizedDeduction } from '../_types';
import { calculateDeductionPercentage } from '../utils/deductionPercentage';

import {
  SelectableDeductionType,
  SelectDeductionTypeTouch,
} from '~/shared/components/select-fields/SelectDeductionTypeTouch';

const FORMAT_WEIGHT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

const FORMAT_PRICE: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 8,
};

const FORMAT_CURRENCY: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

export const DEDUCTION_GRID_CLASS =
  'grid grid-cols-[2fr_120px_120px_120px_44px] items-center gap-1';

export type DialogItemRowDeductionProps = {
  deduction: NormalizedDeduction;
  netWeight: number;
  defaultUnit: Unit;
  touchModeEnabled: boolean;
  labels: {
    percentage: string;
    deductionWeight: string;
    deductionPrice: string;
    subtotal: string;
    deleteDeduction: string;
  };
  emptyDescriptionLabel: string;
  onUpdateType: (
    deductionId: string,
    option?: string,
    entity?: SelectableDeductionType,
  ) => void;
  onPercentageChange: (deductionId: string, value: number) => void;
  onWeightChange: (deductionId: string, value: number) => void;
  onPriceChange: (deductionId: string, value: number) => void;
  onDelete: (deductionId: string) => void;
};

export const DialogItemRowDeduction = React.memo(
  function DialogItemRowDeduction({
    deduction,
    netWeight,
    defaultUnit,
    touchModeEnabled,
    labels,
    emptyDescriptionLabel,
    onUpdateType,
    onPercentageChange,
    onWeightChange,
    onPriceChange,
    onDelete,
  }: DialogItemRowDeductionProps) {
    const isWeightBased = deduction.applicationType === 'Weight';
    const parentNet = netWeight > 0 ? netWeight : 0;
    const deductionPercentage = (() => {
      if (deduction.percentage != null) return deduction.percentage;
      return calculateDeductionPercentage(
        deduction.weightValue?.value ?? 0,
        parentNet,
      );
    })();

    return (
      <div className={cn(DEDUCTION_GRID_CLASS)}>
        <SelectDeductionTypeTouch
          className="flex-1 text-md [&>*]:text-sm"
          color="failure"
          size="xl"
          placeholder={emptyDescriptionLabel}
          value={deduction.deductionTypeId}
          onChange={(option, entity) =>
            onUpdateType(deduction.id, option, entity)
          }
        />
        {isWeightBased ? (
          <TouchNumberField
            aria-label={labels.percentage}
            color="failure"
            value={deductionPercentage}
            onChange={(v) => onPercentageChange(deduction.id, v)}
            formatOptions={FORMAT_WEIGHT}
            touchModeEnabled={touchModeEnabled}
            suffix="%"
          />
        ) : (
          <div />
        )}
        {isWeightBased ? (
          <TouchNumberField
            aria-label={labels.deductionWeight}
            color="failure"
            value={deduction.weightValue?.value ?? 0}
            onChange={(v) => onWeightChange(deduction.id, v)}
            formatOptions={FORMAT_WEIGHT}
            touchModeEnabled={touchModeEnabled}
            suffix={humanReadableShortPricingUnit(defaultUnit)}
          />
        ) : (
          <TouchNumberField
            aria-label={labels.deductionPrice}
            color="failure"
            value={deduction.priceValue?.amount ?? 0}
            onChange={(v) => onPriceChange(deduction.id, v)}
            prefix="$"
            formatOptions={FORMAT_PRICE}
            touchModeEnabled={touchModeEnabled}
          />
        )}
        <TouchNumberField
          aria-label={labels.subtotal}
          color="failure"
          isDisabled
          value={deduction.calculatedAmount?.amount ?? 0}
          prefix="$"
          formatOptions={FORMAT_CURRENCY}
        />
        <TouchButton
          variant="ghost"
          color="failure"
          onPress={() => onDelete(deduction.id)}
          aria-label={labels.deleteDeduction}
          isSelected={false}
        >
          <LucideTrash2 size={16} />
        </TouchButton>
      </div>
    );
  },
);
